import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, HStack, IconButton, Text, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { Add, Delete, DeleteOutlined, DragIndicator } from "@mui/icons-material";

const SortableRow = ({ id, handleDelete, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

    const [isHovered, setIsHovered] = useState(false);

    return (
        <HStack
            ref={setNodeRef}
            style={style}
            rounded="md"
            bg={"#F6F8FA"}
            boxShadow="sm"
            cursor="grab"
            paddingY={"12px"}
            paddingX={"16px"}
            height={"48px"}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            display={"flex"}
            justifyContent={"space-between"}
            onClick={(e) => e.stopPropagation()} // Prevent parent click events
        >
            <HStack
                {...(id !== "Outcome" ? { ...attributes, ...listeners } : {})}
                w={isHovered ? "calc(100% - 40px)" : "100%"}
            >
                <IconButton
                    sx={{
                        padding: "0px",
                        margin: "0px",
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                        backgroundColor: "transparent",
                        fontSize: "20px",
                        minW: "unset",
                    }}
                >
                    <DragIndicator
                        sx={{
                            padding: "0px",
                            margin: "0px",
                            color: isHovered && id != "Outcome" ? "#2584FD" : "#5D5D5D",
                            fontSize: "20px",
                        }}
                    />
                </IconButton>
                <Text
                    fontSize={"16px"}
                    fontWeight={500}
                    color="#1D1D1D"
                >
                    {id}
                </Text>
            </HStack>
            {
                isHovered && id != "Outcome" && (
                    <IconButton
                        sx={{
                            padding: "0px",
                            margin: "0px",
                            "&:hover": {
                                backgroundColor: "#F6F8FA",
                            },
                            backgroundColor: "transparent",
                            fontSize: "24px",
                            minW: "unset",
                            width: "40px",
                        }}
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent event propagation
                            handleDelete(id, index); // Call the delete handler
                        }}
                    >
                        <DeleteOutlined
                            sx={{
                                padding: "0px",
                                margin: "0px",
                                color: "#5D5D5D",
                                fontSize: "22px",
                            }}
                        />
                    </IconButton>
                )
            }
        </HStack>
    );
};

const EditableRow = ({ id, handleDelete, handleEdit, index }) => {
    const { setNodeRef, transform, transition } =
      useSortable({ id });
  
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
  
      const [isHovered, setIsHovered] = useState(false);
  
      return (
          <HStack
              ref={setNodeRef}
              style={style}
              rounded="md"
              bg={"#F6F8FA"}
              boxShadow="sm"
              cursor="grab"
              paddingY={"12px"}
              paddingX={"16px"}
              height={"48px"}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              display={"flex"}
              justifyContent={"space-between"}
              onClick={(e) => e.stopPropagation()} // Prevent parent click events
          >
              <HStack>
                  <IconButton
                      sx={{
                          padding: "0px",
                          margin: "0px",
                          "&:hover": {
                              backgroundColor: "transparent",
                          },
                          backgroundColor: "transparent",
                          fontSize: "20px",
                          minW: "unset",
                      }}
                  >
                      <DragIndicator
                          sx={{
                              padding: "0px",
                              margin: "0px",
                              color: "#5D5D5D",
                              fontSize: "20px",
                          }}
                      />
                  </IconButton>
                    <Text
                        fontSize={"16px"}
                        minW={"100px"}
                        fontWeight={500}
                        color="#1D1D1D"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => handleEdit(id, e.target.innerText, index)}
                        sx={{
                            border: "none",
                            textDecoration: "none",
                            outline: "none",
                        }}
                    >
                        {id}
                    </Text>
              </HStack>
              {
                  isHovered && (
                      <IconButton
                          sx={{
                              padding: "0px",
                              margin: "0px",
                              "&:hover": {
                                  backgroundColor: "#F6F8FA",
                              },
                              backgroundColor: "transparent",
                              fontSize: "24px",
                              minW: "unset",
                              width: "40px",
                          }}
                          onClick={(e) => {
                              e.stopPropagation(); // Prevent event propagation
                              handleDelete(id, index); // Call the delete handler
                          }}
                      >
                          <DeleteOutlined
                              sx={{
                                  padding: "0px",
                                  margin: "0px",
                                  color: "#5D5D5D",
                                  fontSize: "22px",
                              }}
                          />
                      </IconButton>
                  )
              }
          </HStack>
      );
};

const TaskStages = ({items, setItems}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  React.useEffect(() => {
    console.log("Items updated:", items);
  }, [items])

    const handleDelete = (id, index) => {
        setItems((prevItems) => {
            return prevItems.filter((item, i) => !(item === id && i === index));
        });
    };

    const handleAdd = (newStage) => {
        setItems((prevItems) => {
            const newItems = [...prevItems];
            newItems.splice(newItems.length - 1, 0, newStage);
            return newItems;
        });
    };

    const handleEdit = (id, newId, index) => {
        setItems((prevItems) => prevItems.map((item, i) => item === id && i === index  ? newId : item));
    };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <VStack spacing={"16px"} align="stretch">
          {items.map((id, index) => (
            id !== "" ? (
                <SortableRow key={id} id={id} index={index} handleDelete={handleDelete}/>
            ) : (
                <EditableRow key={id} id={id} index={index} handleDelete={handleDelete} handleEdit={handleEdit}/>  
            )
          ))}
          <HStack
            gap={"4px"}
            onClick={() => handleAdd("")}
            _hover={{
                cursor: "pointer",
            }}
          >
            <IconButton
                sx={{
                    padding: "0px",
                    margin: "0px",
                    "&:hover": {
                        backgroundColor: "transparent",
                    },
                    backgroundColor: "transparent",
                    fontSize: "24px",
                    minW: "unset",
                }}
            >
                <Add
                    sx={{
                        padding: "0px",
                        margin: "0px",
                        color: "#2584FD",
                        fontSize: "22px",
                    }}
                />
            </IconButton>
            <Text
                fontSize={"16px"}
                fontWeight={500}
                color="#2584FD"
            >
                Add Stage
            </Text>
          </HStack>
        </VStack>
      </SortableContext>
    </DndContext>
  );
};

export default TaskStages;
